import { useState, useEffect, useRef } from 'react';
import { Button } from './new-components/ui/button';
import { Modal } from './new-components/ui/Modal';
import twoFactorService from '../services/twoFactor';
import toast from 'react-hot-toast';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = 'intro' | 'scan' | 'verify' | 'backup' | 'complete';

export function TwoFactorSetupModal({ isOpen, onClose, onSuccess }: TwoFactorSetupModalProps) {
  const [step, setStep] = useState<Step>('intro');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // QR code and secret
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [showSecret, setShowSecret] = useState(false);

  // Backup codes
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedBackup, setCopiedBackup] = useState(false);

  // OTP input
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setStep('intro');
      setError(null);
      setOtp(['', '', '', '', '', '']);
    }
  }, [isOpen]);

  const handleStartEnrollment = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await twoFactorService.enrollTotp();

      if (response.success && response.data) {
        setQrCode(response.data.qr);
        setSecret(response.data.secret);
        setBackupCodes(response.data.backupCodes);
        setStep('scan');
      } else {
        setError(response.message || 'Failed to start enrollment');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await twoFactorService.verifyTotp(otpCode);

      if (response.success) {
        setStep('backup');
      } else {
        setError(response.message || 'Invalid code. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedBackup(true);
    toast.success('Backup codes copied to clipboard');
    setTimeout(() => setCopiedBackup(false), 3000);
  };

  const handleComplete = () => {
    setStep('complete');
    setTimeout(() => {
      onSuccess?.();
      onClose();
    }, 2000);
  };

  // No `if (!isOpen) return null` — the <Modal>'s Presence drives mount/unmount so the
  // data-[state=closed] exit animation can play. Returning null here would unmount it instantly.
  const stepNumber = 'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-medium text-foreground';

  return (
    <Modal
      open={isOpen}
      onOpenChange={(o) => { if (!o) onClose(); }}
      aria-label="Two-Factor Authentication"
      overlayClassName="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:duration-[var(--dur-base)] data-[state=closed]:duration-[var(--dur-fast)]"
      className="portal-typography fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-card shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:duration-[var(--dur-base)] data-[state=closed]:duration-[var(--dur-fast)] data-[state=open]:ease-[var(--ease-out)] data-[state=closed]:ease-[var(--ease-in)]"
    >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-6">
          <h2 className="text-xl font-semibold text-foreground">Two-Factor Authentication</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-2xl leading-none text-muted-foreground transition-colors md:hover:text-foreground"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Step: Intro */}
          {step === 'intro' && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              <div className="space-y-3 rounded-lg bg-secondary/50 p-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className={stepNumber}>1</span>
                  <p className="text-foreground">Download an authenticator app like Google Authenticator or Authy</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className={stepNumber}>2</span>
                  <p className="text-foreground">Scan the QR code with your authenticator app</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className={stepNumber}>3</span>
                  <p className="text-foreground">Enter the 6-digit code from your app to verify</p>
                </div>
              </div>
              <Button onClick={handleStartEnrollment} disabled={loading} className="w-full">
                {loading ? 'Setting up…' : 'Get started'}
              </Button>
            </div>
          )}

          {/* Step: Scan QR Code */}
          {step === 'scan' && (
            <div className="space-y-4">
              <p className="text-center text-muted-foreground">
                Scan this QR code with your authenticator app
              </p>

              {qrCode && (
                <div className="flex justify-center">
                  <div className="rounded-lg bg-white p-4">
                    <img src={qrCode} alt="2FA QR Code" className="h-48 w-48" />
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="mx-auto text-sm text-foreground underline"
                >
                  {showSecret ? 'Hide' : 'Show'} manual entry code
                </button>

                {showSecret && (
                  <div className="mt-2 rounded-lg bg-secondary/50 p-3">
                    <code className="break-all font-mono text-sm text-foreground">{secret}</code>
                  </div>
                )}
              </div>

              <Button onClick={() => setStep('verify')} className="w-full">
                Continue
              </Button>
            </div>
          )}

          {/* Step: Verify OTP */}
          {step === 'verify' && (
            <div className="space-y-4">
              <p className="text-center text-muted-foreground">
                Enter the 6-digit code from your authenticator app
              </p>

              <div className="flex justify-center gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="h-14 w-12 rounded-lg border-2 border-border bg-background text-center font-mono text-2xl text-foreground outline-none focus:border-foreground"
                    maxLength={1}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <Button onClick={handleVerifyOtp} disabled={loading || otp.join('').length !== 6} className="w-full">
                {loading ? 'Verifying…' : 'Verify'}
              </Button>

              <button
                onClick={() => setStep('scan')}
                className="w-full text-sm text-muted-foreground transition-colors md:hover:text-foreground"
              >
                Back to QR code
              </button>
            </div>
          )}

          {/* Step: Backup Codes */}
          {step === 'backup' && (
            <div className="space-y-4">
              <h3 className="text-center text-lg font-semibold text-foreground">Two-factor authentication enabled</h3>

              <div className="rounded-lg bg-secondary/50 p-4">
                <p className="mb-1 text-sm font-medium text-foreground">Save your backup codes</p>
                <p className="text-sm text-muted-foreground">
                  These codes can be used to access your account if you lose your authenticator device. Each code can only be used once.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-secondary/50 p-4 font-mono text-sm text-foreground">
                {backupCodes.map((code, index) => (
                  <div key={index} className="py-1 text-center">
                    {code}
                  </div>
                ))}
              </div>

              <Button onClick={handleCopyBackupCodes} variant="outline" className="w-full">
                {copiedBackup ? 'Copied!' : 'Copy backup codes'}
              </Button>

              <Button onClick={handleComplete} className="w-full">
                I've saved my backup codes
              </Button>
            </div>
          )}

          {/* Step: Complete */}
          {step === 'complete' && (
            <div className="py-8 text-center">
              <h3 className="mb-2 text-xl font-semibold text-foreground">All set</h3>
              <p className="text-muted-foreground">
                Two-factor authentication has been enabled for your account.
              </p>
            </div>
          )}
        </div>
    </Modal>
  );
}

export default TwoFactorSetupModal;
